import { Fragment, memo, useCallback, useEffect, useState } from "react";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import {
  Breadcrumbs,
  Button,
  Division,
  Flex,
  Form,
  FormInstance,
  Heading,
  Icon,
  Icons,
  Modal,
  Spin,
  useToast,
} from "@ir/ui";
import "./RaiseDispute.scss";
import { RaiseDisputeDeductionUseCase } from "@/core/usecases/deduction";
import { DeductionDisputeRepository } from "@/infrastructure/repositories/deduction/DeductionDispute.repository";
import { RaiseDisputeDeductionDTO } from "@/core/dto";
import { useConfirm, useEventBusSubscriber } from "@/hooks";
import { DisputeEvents, DisputeStatusUpdatePayload } from "@/core/events/disputeEventsMap";
import {
  RaiseDisputeFormValues,
  RaiseDisputeModalProps,
  DisputeDataForSubmit,
  RaiseDisputeDotfoodsFormValues,
  RaiseDisputeUnfiFormValues,
} from "./RaiseDisputeInterface";
import RiseDisputeDotfoodsForm from "./RaiseDisputeDotfoodsForm";
import RaiseDisputeKeheForm from "./RaiseDisputeKeheForm";
import { useRemittance, useRemittanceItem } from "@/presentation/deduction/hooks";
import { createLogger } from "@/infrastructure/services/logger";
import RiseDisputeUnfiForm from "./RaiseDisputeUnfiForm";
import { getRemittanceItemNetAmount } from "@/presentation/deduction/utils/getRemittanceItemNetAmount";

const client = GraphQlAdapter.getInstance().getClient();

const DISPUTE_PREVIEW_BRANDING = {
  brandLabel: "UNFI",
  title: "UNFI Supplier Dispute Form",
  notice:
    "All UNFI Natural Deduction Disputes should be submitted to Deductions@UNFI.com and all UNFI Natural payment terms issues should be submitted to your UNFI AP Contact.",
};

const DisputeModal = (raiseDisputeModalProps: RaiseDisputeModalProps) => {
  const logger = createLogger("RaiseDisputeModal");
  const [remittanceItemNetAmount, setRemittanceItemNetAmount] = useState<number | null>(null);
  const [isDisputing, setIsDisputing] = useState(false);
  const [isUnfiTemplateViewMode, setIsUnfiTemplateViewMode] = useState<boolean>(false);
  const [unfiTemplateData, setUnfiTemplateData] = useState<any>(null);
  const confirm = useConfirm();
  const { remittanceItems } = useRemittanceItem();
  const { remittances } = useRemittance();
  const { deduction, checkNumber, claimNumber, onDisputeSubmitted, onDisputeStatusUpdate } = raiseDisputeModalProps;
  const { error: errorToast } = useToast();

  const [keheForm] = Form.useForm<RaiseDisputeFormValues>();
  const [dotfoodsForm] = Form.useForm<RaiseDisputeDotfoodsFormValues>();
  const [unfiForm] = Form.useForm<RaiseDisputeUnfiFormValues>();

  const handleCloseModal = useCallback(() => {
    raiseDisputeModalProps.setIsDisputeModalOpen(false);
    keheForm?.resetFields();
    dotfoodsForm?.resetFields();
    unfiForm?.resetFields();
  }, [keheForm, dotfoodsForm, unfiForm, raiseDisputeModalProps]);

  useEffect(() => {
    setRemittanceItemNetAmount(getRemittanceItemNetAmount(deduction?.remittanceItemId, remittanceItems, remittances));
  }, [deduction?.remittanceItemId, remittanceItems, remittances]);

  useEventBusSubscriber(DisputeEvents.DISPUTE_STATUS_UPDATE, (payload: DisputeStatusUpdatePayload) => {
    if (payload.deductionInfo.deductionId !== deduction.id) return;

    if (payload.isDisputeFiled) {
      onDisputeStatusUpdate();
    }
  });

  const getHeaderContent = () => {
    return (
      <Flex direction="column" gap={0.25}>
        <Heading level={6} className="dispute-modal-heading">
          Raise Dispute
        </Heading>
        <Breadcrumbs items={[{ label: `Check #${checkNumber}` }, { label: `${claimNumber}` }]} />
      </Flex>
    );
  };

  const getFooterContent = () => {
    let currentForm: FormInstance;

    switch (deduction?.purchaserKey) {
      case "DOT_FOODS":
        currentForm = dotfoodsForm;
        break;

      case "KEHE":
        currentForm = keheForm;
        break;

      case "UNFI":
      case "SUPERVALU":
        currentForm = unfiForm;
        break;

      default:
        return null;
    }

    return (
      <Division className="d-flex justify-content-end">
        <Button variant="outline" color="grey" onPress={handleCloseModal} isDisabled={isDisputing}>
          Cancel
        </Button>
        <Button color="primary" onPress={() => currentForm.submit()} isDisabled={isDisputing}>
          Continue
        </Button>
      </Division>
    );
  };

  const handleUnfiDisputePreview = (disputeData: DisputeDataForSubmit) => {
    setIsUnfiTemplateViewMode(true);

    setUnfiTemplateData(disputeData);
  };

  // Submit for Purchaser
  const handleDisputeSubmit = async (disputeData: DisputeDataForSubmit, isConfirmationNeeded: boolean = true) => {
    if (isConfirmationNeeded) {
      // confirmation modal
      const confirmResult = await confirm({
        title: "Confirm Submission",
        header: (
          <Division className="confirm-dialog-header">
            <Breadcrumbs items={[{ label: `Check #${checkNumber}` }, { label: `${claimNumber}` }]} />
          </Division>
        ),
        message:
          "You're about to submit this dispute for review. Please ensure all details are correct before proceeding.",
        confirmText: "Submit Dispute",
        confirmButtonVariant: "solid",
        confirmButtonColor: "primary",
        cancelText: "Back",
        cancelButtonVariant: "outline",
        cancelButtonColor: "primary",
      });

      if (!confirmResult) {
        return;
      }
    }

    let response;

    try {
      setIsDisputing(true);
      response = await new RaiseDisputeDeductionUseCase(new DeductionDisputeRepository(client)).execute(
        new RaiseDisputeDeductionDTO({
          deductionId: deduction.id,
          deductionTypeId: deduction.deductionTypeId,
          purchaserId: deduction.purchaserId,
          purchaserKey: deduction.purchaserKey,
          remittanceItemId: deduction.remittanceItemId,
          disputeTypeId: Number(disputeData.disputeTypeId),
          disputeSubTypeId: disputeData.disputeSubTypeId ? Number(disputeData.disputeSubTypeId) : undefined,
          disputeAmount: disputeData.disputeAmount,
          totalAmount: disputeData.totalAmount,
          contactInfo: disputeData.contactInfo,
          disputeRequestMessage: disputeData.disputeRequestMessage,
          documentIds: disputeData.documentIds,
          portalKey: disputeData.portalKey,
          brandName: disputeData.brandName,
          vendorNumber: disputeData.vendorNumber,
          distributionCenter: disputeData.distributionCenter,
          dataProviderType: disputeData.dataProviderType,
          poNumber: disputeData.poNumber,
          checkNumber: disputeData.checkNumber,
          invoiceNumber: disputeData.invoiceNumber,
        })
      );
      if (response?.id) {
        handleCloseModal();
        onDisputeSubmitted();
      }
    } catch (error) {
      logger.error("Error while filing dispute", error);
      errorToast({
        content: "Error while filing dispute.",
        icon: <Icon name={Icons.AlertCircle} />,
      });
    } finally {
      setIsDisputing(false);
    }
  };

  // Open Form Based on Purchaser Key
  const getFromByPurchaserKey = (purchaserKey: string) => {
    switch (purchaserKey) {
      case "DOT_FOODS":
        return (
          <RiseDisputeDotfoodsForm
            {...raiseDisputeModalProps}
            disputeForm={dotfoodsForm}
            onDisputeSubmit={handleDisputeSubmit}
            deductionInvoiceAmount={raiseDisputeModalProps.deductionInvoiceAmount}
            remittanceItemNetAmount={remittanceItemNetAmount}
          />
        );

      case "KEHE":
        return (
          <RaiseDisputeKeheForm
            {...raiseDisputeModalProps}
            disputeForm={keheForm}
            onDisputeSubmit={handleDisputeSubmit}
            deductionInvoiceAmount={raiseDisputeModalProps.deductionInvoiceAmount}
            remittanceItemNetAmount={remittanceItemNetAmount}
          />
        );

      case "UNFI":
      case "SUPERVALU":
        return (
          <RiseDisputeUnfiForm
            {...raiseDisputeModalProps}
            disputeForm={unfiForm}
            deduction={raiseDisputeModalProps.deduction}
            onDisputeSubmit={handleDisputeSubmit}
            onUnfiEmailPreview={handleUnfiDisputePreview}
            deductionInvoiceAmount={raiseDisputeModalProps.deductionInvoiceAmount}
            remittanceItemNetAmount={remittanceItemNetAmount}
          />
        );

      default:
        return <Division>You are not authorized to raise dispute for this purchaser.</Division>;
    }
  };

  const handleUnfiDisputeSubmit = () => {
    setIsUnfiTemplateViewMode(false);
    handleDisputeSubmit(unfiTemplateData, false);
  };

  const handleFinalClose = () => {
    setIsUnfiTemplateViewMode(false);
  };

  return (
    <Fragment>
      {isUnfiTemplateViewMode && (
        <Modal
          wrapClassName="unfi-dispute-preview-modal"
          isOpen={isUnfiTemplateViewMode}
          closable
          headerContent={
            <Flex align="center" justifyContent="space-between" style={{ flex: 1 }}>
              <Heading level={6}>Dispute Preview</Heading>
              <Button variant="clear" onPress={handleFinalClose}>
                <Icon color="grey" name={Icons.Close} />
              </Button>
            </Flex>
          }
          footerContent={
            <Flex>
              <Button onPress={handleFinalClose}>Close</Button>
              <Button onPress={handleUnfiDisputeSubmit}>Submit</Button>
            </Flex>
          }
          isFullScreen={true}
          zIndex={13}
        >
          <div className="unfi-dispute-preview">
            <table className="unfi-dispute-preview__table">
              <tbody>
                <tr className="unfi-dispute-preview__brand-row">
                  <td colSpan={2} className="unfi-dispute-preview__logo-cell">
                    <span className="unfi-dispute-preview__logo-text">{DISPUTE_PREVIEW_BRANDING.brandLabel}</span>
                  </td>
                  <td colSpan={3} className="unfi-dispute-preview__title-cell">
                    {DISPUTE_PREVIEW_BRANDING.title}
                  </td>
                  <td colSpan={1} className="unfi-dispute-preview__logo-cell unfi-dispute-preview__logo-cell--right">
                    <span className="unfi-dispute-preview__logo-text">{DISPUTE_PREVIEW_BRANDING.brandLabel}</span>
                  </td>
                  <td colSpan={1} style={{ width: "400px" }} className="unfi-dispute-preview__logo-cell unfi-dispute-preview__logo-cell--right">

                  </td>
                </tr>

                <tr className="unfi-dispute-preview__notice-row">
                  <td colSpan={7}>{DISPUTE_PREVIEW_BRANDING.notice}</td>
                </tr>

                <tr className="unfi-dispute-preview__info-row">
                  <td colSpan={7} className="unfi-dispute-preview__info-cell">
                    Fields marked with <strong style={{ color: "#f04438" }}>**</strong> are required with any dispute
                    submitted. Fields marked with <strong style={{ color: "#f04438" }}>*</strong> are required if
                    applicable.
                  </td>

                </tr>

                <tr className="unfi-dispute-preview__field-row">
                  <td colSpan={3} style={{ border: "none", padding: "0" }}>
                    <table className="unfi-dispute-preview__field-table">
                      <tr className="unfi-dispute-preview__field-row">
                        <th className="unfi-dispute-preview__field-label">
                          Company Name <strong style={{ color: "#f04438" }}>**</strong>
                        </th>
                        <td colSpan={2} className="unfi-dispute-preview__field-value">
                          {unfiTemplateData?.brandName}
                        </td>
                      </tr>

                      <tr className="unfi-dispute-preview__field-row">
                        <th className="unfi-dispute-preview__field-label">
                          Requestor's Name <strong style={{ color: "#f04438" }}>**</strong>
                        </th>
                        <td colSpan={2} className="unfi-dispute-preview__field-value">
                          {unfiTemplateData?.contactInfo?.name}
                        </td>
                      </tr>

                      <tr className="unfi-dispute-preview__field-row">
                        <th className="unfi-dispute-preview__field-label">
                          Requestor's eMail <strong style={{ color: "#f04438" }}>**</strong>
                        </th>
                        <td colSpan={2} className="unfi-dispute-preview__field-value">
                          {unfiTemplateData?.contactInfo?.email}
                        </td>
                      </tr>

                      <tr className="unfi-dispute-preview__field-row">
                        <th className="unfi-dispute-preview__field-label">
                          Remit / Lawson Number <strong style={{ color: "#f04438" }}>**</strong>
                        </th>
                        <td className="unfi-dispute-preview__field-value">
                          {unfiTemplateData?.vendorNumber}
                        </td>
                        <td className="" style={{ border: "none" }}></td>
                      </tr>
                    </table>
                  </td>
                  <td colSpan={1} style={{ border: "none", padding: "0" }}></td>
                  <td colSpan={3} style={{ border: "none", padding: "0", fontStyle: "italic", color: "#1a1a1a" }} className="unfi-dispute-preview__info-cell unfi-dispute-preview__info-cell--right">
                    Please allow 30 days from dispute form submission before following up for status of resolution.
                    <br />
                    Do not use this form for submitting a re-dispute, backup requests or payment inquiry.
                    <br />
                    Attach any supporting documentation that will validate your claim with your dispute email, such as
                    the Bill of Lading for quantity discrepancies or any corresponding communication.
                    <br />
                    Additional lines needed for dispute information may be listed on the Additional Sheet.
                  </td>
                </tr>

                <tr className="unfi-dispute-preview__instruction-row">
                  <td colSpan={7}>
                    Please list each Deduction Or Invoice separately, on its own line, and provide the following
                    information for each:
                  </td>
                </tr>

                <tr className="unfi-dispute-preview__col-header-row">
                  <td>
                    Deduction Number or Invoice Number <strong style={{ color: "#f04438" }}>**</strong>
                  </td>
                  <td>
                    Check Number <strong style={{ color: "#f04438" }}>*</strong>
                  </td>
                  <td>
                    Deduction Amount <strong style={{ color: "#f04438" }}>**</strong>
                  </td>
                  <td>
                    Disputed Amount <strong style={{ color: "#f04438" }}>**</strong>
                  </td>
                  <td>
                    PO Number <strong style={{ color: "#f04438" }}>*</strong>
                  </td>
                  <td>
                    Warehouse <strong style={{ color: "#f04438" }}>*</strong>
                  </td>
                  <td>
                    Reason For Dispute <strong style={{ color: "#f04438" }}>**</strong>
                  </td>
                </tr>

                <tr className="unfi-dispute-preview__data-row">
                  <td>{unfiTemplateData?.invoiceNumber}</td>
                  <td>{unfiTemplateData?.checkNumber}</td>
                  <td>{unfiTemplateData?.totalAmount}</td>
                  <td>{unfiTemplateData?.disputeAmount}</td>
                  <td>{unfiTemplateData?.poNumber}</td>
                  <td>{unfiTemplateData?.distributionCenter}</td>
                  <td>{unfiTemplateData?.disputeRequestMessage}</td>
                </tr>
              </tbody>
              <tbody className="unfi-dispute-preview__empty-body">
                {Array.from({ length: 14 }).map((_, i) => (
                  <tr key={`empty-row-${i} - ${new Date()}`} className="unfi-dispute-preview__empty-row">
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                ))}
              </tbody>
              <tbody>
                <tr className="unfi-dispute-preview__footer-row">
                  <td colSpan={7}>ADDITIONAL DISPUTE INFORMATION OR SUMMARY</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      <Modal
        isOpen={raiseDisputeModalProps.isDisputeModalOpen}
        zIndex={12}
        onClose={handleCloseModal}
        headerContent={getHeaderContent()}
        footerContent={isDisputing ? false : getFooterContent()}
        wrapClassName="upload-remittance-modal"
        closable={!isDisputing}
        width="960px"
      >
        {isDisputing ? (
          <Flex align="center" justifyContent="center" style={{ minHeight: "200px", height: "100%" }}>
            <Spin spinning={isDisputing} tip="Submitting..." size="large" />
          </Flex>
        ) : (
          <Division className="dispute-form-container">{getFromByPurchaserKey(deduction?.purchaserKey)}</Division>
        )}
      </Modal>
    </Fragment>
  );
};

export const RaiseDisputeModal = memo(DisputeModal);
